import React, { useState } from 'react';
import { Counter, Text } from '@vibe/core';
import { NavigationChevronDown, NavigationChevronRight } from '@vibe/icons';
import { getGroupedTemplates } from '../../config/timesheet-grouping';
import { useApprovalStore } from '../../stores/approvalStore';
import type { TimesheetGroupBy } from '../../types/timesheet.types';

export interface TimesheetSidebarProps {
  activeGroupBy: TimesheetGroupBy;
  onSelect: (groupBy: TimesheetGroupBy) => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
  /** When false, hide the "Approvals" sidebar category */
  showApprovals?: boolean;
}

const TimesheetSidebar: React.FC<TimesheetSidebarProps> = ({
  activeGroupBy,
  onSelect,
  collapsed,
  onToggleCollapse,
  showApprovals = false,
}) => {
  const allGrouped = getGroupedTemplates();
  const grouped = showApprovals
    ? allGrouped
    : Object.fromEntries(
        Object.entries(allGrouped).filter(([key]) => key !== 'approvals'),
      );
  const pendingCount = useApprovalStore((s) => s.pendingCount);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(['user_timesheets', 'board_timesheets', 'progress_reports', 'timesheet_reports', 'approvals']),
  );

  const toggleCategory = (key: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  if (collapsed) {
    return (
      <div className="rp-ts-sidebar rp-ts-sidebar--collapsed">
        <button
          className="rp-ts-sidebar__toggle"
          onClick={onToggleCollapse}
          title="Expand sidebar"
        >
          <NavigationChevronRight />
        </button>
      </div>
    );
  }

  return (
    <div className="rp-ts-sidebar">
      <div className="rp-ts-sidebar__header">
        <Text type={Text.types.TEXT1} weight={Text.weights.BOLD}>
          Templates
        </Text>
        <button
          className="rp-ts-sidebar__toggle"
          onClick={onToggleCollapse}
          title="Collapse sidebar"
        >
          <NavigationChevronDown />
        </button>
      </div>

      <div className="rp-ts-sidebar__content">
        {Object.entries(grouped).map(([categoryKey, category]) => {
          const isExpanded = expandedCategories.has(categoryKey);
          return (
            <div key={categoryKey} className="rp-ts-sidebar__category">
              <button
                className="rp-ts-sidebar__category-header"
                onClick={() => toggleCategory(categoryKey)}
              >
                {isExpanded ? (
                  <NavigationChevronDown />
                ) : (
                  <NavigationChevronRight />
                )}
                <Text type={Text.types.TEXT2} weight={Text.weights.MEDIUM}>
                  {category.label}
                </Text>
              </button>

              {isExpanded && (
                <div className="rp-ts-sidebar__items">
                  {category.items.map((item) => (
                    <button
                      key={item.key}
                      className={`rp-ts-sidebar__item ${
                        activeGroupBy === item.key
                          ? 'rp-ts-sidebar__item--active'
                          : ''
                      }`}
                      onClick={() => onSelect(item.key)}
                      title={item.description}
                    >
                      <Text type={Text.types.TEXT2}>{item.label}</Text>
                      {item.key === 'approval_pending' && pendingCount > 0 && (
                        <Counter
                          count={pendingCount}
                          size={Counter.sizes.SMALL}
                          color={Counter.colors.PRIMARY}
                        />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TimesheetSidebar;
